/**
 * FleetKpiStrip — top-of-map ops-room KPI band for Live Shipment Map.
 *
 * Shows: in-transit count, delivered-today, on-time %, avg transit hrs
 * (this week vs last week w/ delta arrow), and a 14-day SVG sparkline
 * of shipped/delivered per day.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Truck, CheckCircle, Timer, ClockClockwise, TrendUp, TrendDown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sparkline = ({ data, accent = '#002FA7', secondary = '#10b981' }) => {
  if (!data || data.length === 0) return null;
  const width = 220;
  const height = 48;
  const pad = 4;
  const max = Math.max(1, ...data.map(d => Math.max(d.dispatched, d.delivered)));
  const step = (width - 2 * pad) / Math.max(1, data.length - 1);
  const yFor = (v) => height - pad - ((v / max) * (height - 2 * pad));
  const line = (series) => data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * step} ${yFor(series(d))}`).join(' ');
  const areaPath = `${line(d => d.dispatched)} L ${pad + (data.length - 1) * step} ${height - pad} L ${pad} ${height - pad} Z`;
  return (
    <svg width={width} height={height} role="img" aria-label="14-day shipments sparkline" data-testid="fleet-sparkline">
      <path d={areaPath} fill={accent} fillOpacity="0.1" />
      <path d={line(d => d.dispatched)} fill="none" stroke={accent} strokeWidth="1.6" />
      <path d={line(d => d.delivered)} fill="none" stroke={secondary} strokeWidth="1.4" strokeDasharray="2 2" />
      {data.map((d, i) => (
        <circle key={i} cx={pad + i * step} cy={yFor(d.dispatched)} r={1.5} fill={accent} />
      ))}
    </svg>
  );
};

const KpiCard = ({ label, value, sub, accent = 'slate', icon: Icon, testid }) => {
  const colorMap = {
    slate: 'text-foreground',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    blue: 'text-brand',
    rose: 'text-rose-700',
  };
  return (
    <div className="flex-1 min-w-[140px] px-3 py-2 border-r border-border last:border-r-0" data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
        {Icon && <Icon size={11} weight="bold" />}
        {label}
      </div>
      <div className={`text-xl font-bold font-mono mt-0.5 ${colorMap[accent]}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground font-mono">{sub}</div>}
    </div>
  );
};

const FleetKpiStrip = () => {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/dispatch/fleet-kpis`, { withCredentials: true });
        if (mounted) setKpi(r.data);
      } catch {
        // silent
      } finally { if (mounted) setLoading(false); }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (loading && !kpi) {
    return (
      <div className="bg-card border border-border rounded-lg border-b-0 px-4 py-2 text-xs text-muted-foreground" data-testid="fleet-kpi-strip-loading">
        Loading fleet KPIs…
      </div>
    );
  }
  if (!kpi) return null;

  const delta = kpi.avg_transit_delta_hours || 0;
  const deltaIcon = delta === 0 ? null : delta < 0 ? TrendDown : TrendUp;
  const deltaColor = delta === 0 ? 'text-muted-foreground' : delta < 0 ? 'text-emerald-600' : 'text-rose-600';

  return (
    <div className="bg-card border border-border rounded-lg border-b-0" data-testid="fleet-kpi-strip">
      <div className="flex flex-wrap items-stretch divide-slate-200">
        <KpiCard
          testid="kpi-in-transit"
          label="In-transit" accent="blue" icon={Truck}
          value={kpi.in_transit_count}
          sub="live dispatches"
        />
        <KpiCard
          testid="kpi-delivered-today"
          label="Delivered today" accent="emerald" icon={CheckCircle}
          value={kpi.delivered_today_count}
          sub={new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: '2-digit' })}
        />
        <KpiCard
          testid="kpi-on-time"
          label="On-time" accent={kpi.on_time_pct >= 90 ? 'emerald' : kpi.on_time_pct >= 75 ? 'amber' : 'rose'}
          icon={Timer}
          value={`${kpi.on_time_pct}%`}
          sub={`n=${kpi.on_time_sample_size} (28d)`}
        />
        <KpiCard
          testid="kpi-avg-transit"
          label="Avg transit" accent="slate" icon={ClockClockwise}
          value={`${kpi.avg_transit_hours_this_week}h`}
          sub={
            <span className="flex items-center gap-1">
              <span>vs {kpi.avg_transit_hours_last_week}h last wk</span>
              {deltaIcon && (
                <span className={`inline-flex items-center gap-0.5 ${deltaColor} font-bold`} data-testid="kpi-avg-transit-delta">
                  {React.createElement(deltaIcon, { size: 10, weight: 'bold' })}
                  {Math.abs(delta)}h
                </span>
              )}
            </span>
          }
        />
        <div className="flex-1 min-w-[220px] px-3 py-2" data-testid="kpi-sparkline">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <span>14-day shipments</span>
            <span className="flex items-center gap-2 text-[9px] font-mono text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-0.5" style={{ background: '#002FA7' }} />
                Dispatched
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-0.5" style={{ background: '#10b981', borderTop: '1px dashed' }} />
                Delivered
              </span>
            </span>
          </div>
          <div className="mt-0.5">
            <Sparkline data={kpi.shipments_per_day_14d || []} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetKpiStrip;
